/* ============================================================
   TASK-329 (owner decision 2026-09-07): the price-provenance line shown
   under every AI plan card (PlanSelectScreen, Plans.jsx) and the current
   plan card (Settings.jsx BillingTab) — "N connected · <Plan> includes K ->
   +(N-K) x €3.99 = €X/mo", or the plain "<Plan> includes K" once nothing is
   billed beyond the plan. One shared component so all three screens render
   byte-identical copy/markup instead of three hand-rolled copies.

   The "N connected" segment opens <ConnectedAccountsSheet> — but ONLY once
   `shouldOfferAccountsSheet(pricing)` is true (mode 'priced' AND extra > 0
   — round 2 fix: the single shared gate, not a locally re-derived
   `pricing.extra > 0` check that could drift from it); at/under the
   included count, or outside 'priced' mode entirely, it renders as plain
   text, nothing to click through to. It is a `role="button"` <span>, not a
   real <button>, on purpose: PlanSelectScreen's AI card (the whole
   .ck-card) is itself a <button> — nesting a real <button> inside it is
   invalid HTML and would also submit the outer card's onClick (selecting
   that plan) on every bubbled click. stopPropagation() below is what
   actually prevents that; the span-not-button choice is only for HTML
   validity, kept the same on every surface (including Plans.jsx/
   Settings.jsx, where the ancestor is a plain <div>) so all three render
   identical markup.

   `interactive = false` (round 2 fix, PlanSelect a11y minor): renders the
   full provenance line as plain, non-interactive text even when the sheet
   WOULD otherwise be offered — used only by PlanSelectScreen, whose AI
   cards are themselves `role="radio"` buttons (a nested interactive
   `role="button"` there is invalid content: interactive-inside-interactive
   confuses assistive tech and the outer .ck-card::after tap-target
   overlaps the card's own text). That screen instead renders ONE separate,
   genuinely-a-sibling control outside the radio button to open the sheet.
   ============================================================ */
import React from 'react';
import { shouldOfferAccountsSheet } from '../../lib/socialAddon.js';

export default function SocialAddonProvenanceLine({ pricing, onOpenSheet, className = '', interactive = true }) {
  if (!pricing || !pricing.provenanceLine) return null;

  if (!interactive || !shouldOfferAccountsSheet(pricing)) {
    return <p className={`social-addon-line ${className}`}>{pricing.provenanceLine}</p>;
  }

  const openSheet = (e) => {
    e.stopPropagation();
    onOpenSheet?.(e);
  };
  const onKeyDown = (e) => {
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      openSheet(e);
    }
  };

  return (
    <p className={`social-addon-line ${className}`}>
      <span
        role="button"
        tabIndex={0}
        className="social-addon-chip"
        onClick={openSheet}
        onKeyDown={onKeyDown}
        aria-haspopup="dialog"
      >
        {pricing.connectedSegment}
      </span>
      <span className="social-addon-suffix">{pricing.provenanceSuffix}</span>
    </p>
  );
}
